import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCustomerData } from '../hooks/useCustomerData';
import { Link } from 'react-router-dom';

type ChangeType = 'positive' | 'negative' | 'neutral';

const Dashboard: React.FC = () => {
  const { currentUser } = useAuth();
  const { profile, urls, loading } = useCustomerData();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  const totalClicks = Array.isArray(urls) ? urls.reduce((sum, url) => sum + url.access_count, 0) : 0;
  const averageClickRate = Array.isArray(urls) && urls.length > 0 ? (totalClicks / urls.length).toFixed(1) : '0';
  const usagePercentage = profile?.usage?.limit === -1 ? 0 :
    ((profile?.usage?.current_month || 0) / (profile?.usage?.limit || 1)) * 100;

  const stats = [
    {
      name: 'URLs Created',
      value: (profile?.usage?.urls_created || 0).toString(),
      icon: (
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
        </svg>
      ),
      change: `${profile?.usage?.current_month || 0} this month`,
      changeType: 'neutral' as ChangeType,
    },
    {
      name: 'Total Clicks',
      value: (profile?.usage?.total_clicks || 0).toString(),
      icon: (
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      change: 'All time',
      changeType: 'neutral' as ChangeType,
    },
    {
      name: 'Avg. Click Rate',
      value: `${averageClickRate}`,
      icon: (
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
        </svg>
      ),
      change: 'Per URL',
      changeType: 'neutral' as ChangeType,
    },
  ];

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
        <p className="mt-2 text-sm text-gray-700">
          Welcome back, {currentUser?.email}
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 mb-8">
        {stats.map((stat) => (
          <div key={stat.name} className="bg-white rounded-macos-lg shadow-macos p-6 border border-gray-100">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-10 w-10 rounded-macos bg-primary-50 text-primary-500">
                  {stat.icon}
                </div>
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                <div className="flex items-baseline">
                  <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
                  <p className={`ml-2 text-sm font-medium ${stat.changeType === 'positive' ? 'text-green-600' :
                    stat.changeType === 'negative' ? 'text-red-600' : 'text-gray-500'
                    }`}>
                    {stat.change}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-macos-lg shadow-macos p-6 border border-gray-100">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
          <div className="space-y-3">
            <Link
              to="/urls"
              className="flex items-center p-3 rounded-macos hover:bg-gray-50 transition-colors group"
            >
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-8 w-8 rounded-macos bg-primary-100 text-primary-600 group-hover:bg-primary-200">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">Create New URL</p>
                <p className="text-sm text-gray-500">Shorten a new URL for your beacons</p>
              </div>
            </Link>

            <Link
              to="/analytics"
              className="flex items-center p-3 rounded-macos hover:bg-gray-50 transition-colors group"
            >
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-8 w-8 rounded-macos bg-primary-100 text-primary-600 group-hover:bg-primary-200">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
                  </svg>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">View Analytics</p>
                <p className="text-sm text-gray-500">Check your URL performance</p>
              </div>
            </Link>
          </div>
        </div>

        <div className="bg-white rounded-macos-lg shadow-macos p-6 border border-gray-100">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Account Status</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Current Plan</span>
              <span className="text-sm font-medium text-gray-900 capitalize">
                {profile?.plan || 'Free'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">URLs This Month</span>
              <span className="text-sm font-medium text-gray-900">
                {profile?.usage?.current_month || 0} / {profile?.usage?.limit === -1 ? '∞' : profile?.usage?.limit || 100}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(usagePercentage, 100)}%` }}
              />
            </div>
            {profile?.plan === 'free' && (
              <Link
                to="/billing"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-macos text-primary-700 bg-primary-100 hover:bg-primary-200 transition-colors"
              >
                Upgrade Plan
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-macos-lg shadow-macos border border-gray-100">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Recent URLs</h3>
        </div>
        <div className="p-6">
          {!Array.isArray(urls) || urls.length === 0 ? (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No URLs yet</h3>
              <p className="mt-1 text-sm text-gray-500">Get started by creating your first short URL.</p>
              <div className="mt-6">
                <Link
                  to="/urls"
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-macos text-white bg-primary-500 hover:bg-primary-600 transition-colors"
                >
                  Create URL
                </Link>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {Array.isArray(urls) && urls.slice(0, 5).map((url) => (
                <div key={url.short_code} className="flex items-center justify-between p-3 bg-gray-50 rounded-macos">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {url.short_url}
                    </p>
                    <p className="text-sm text-gray-500 truncate">
                      → {url.destination_url}
                    </p>
                  </div>
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span>{url.access_count} clicks</span>
                    <span>{new Date(url.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
              {Array.isArray(urls) && urls.length > 5 && (
                <div className="text-center pt-4">
                  <Link
                    to="/urls"
                    className="text-sm text-primary-600 hover:text-primary-500 font-medium"
                  >
                    View all {Array.isArray(urls) ? urls.length : 0} URLs →
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;